import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Compass, FileCheck2, Plane, BedDouble, UtensilsCrossed, ShieldCheck,
  GraduationCap, Stethoscope, Heart, ArrowRight, Quote, MessageSquareHeart,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import TestimonialWall from "../components/TestimonialWall";

const HERO_IMG = "https://images.unsplash.com/photo-1778230123972-07eafe417fc4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwc3R1ZGVudHMlMjBzbWlsaW5nfGVufDB8fHx8MTc4Mjg1MDU5Mnww&ixlib=rb-4.1.0&q=85";
const CAMPUS_IMG = "https://images.unsplash.com/photo-1591123120675-6f7f1aae0e5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxiZWF1dGlmdWwlMjB1bml2ZXJzaXR5JTIwY2FtcHVzfGVufDB8fHx8MTc4Mjg1MDU5Mnww&ixlib=rb-4.1.0&q=85";
const SAFFRON_IMG = "https://images.unsplash.com/photo-1615885108069-7d5bef9a7e22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmb29kJTIwc2FmZnJvbnxlbnwwfHx8fDE3ODI4NTA1OTJ8MA&ixlib=rb-4.1.0&q=85";

const services = [
  { icon: Compass, title: "Counselling & Guidance", body: "Personal counsellors who understand MBBS abroad, fees, careers and the right university match." },
  { icon: FileCheck2, title: "Admission & Documentation", body: "End-to-end paperwork — university applications, visa documents, attestation and verification." },
  { icon: Plane, title: "Visa & Pre-Departure", body: "Visa support, ticket planning, airport assistance and a pre-departure briefing so nothing is missed." },
  { icon: BedDouble, title: "Accommodation Support", body: "Safe, clean and comfortable hostels — handpicked, inspected and managed with care." },
  { icon: UtensilsCrossed, title: "Atmia Saffron Kingdom", body: "Home-style Indian & Gujarati meals in Georgia — taste, hygiene and timely service, every day." },
  { icon: ShieldCheck, title: "Local Support in Georgia", body: "An on-ground team in Tbilisi to settle students, help with any issue and keep parents updated." },
];

const stats = [
  { value: "500+", label: "Students placed" },
  { value: "10+", label: "Years of guidance" },
  { value: "SEU", label: "Trusted partner" },
  { value: "24/7", label: "Local support" },
];

export default function Landing() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-sand-50 text-stone-900">
      <Header />

      {/* HERO */}
      <section data-testid="hero-section" className="relative grain-bg overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-28 grid lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-stone-200 text-[11px] sm:text-xs tracking-[0.18em] uppercase text-saffron-600 font-medium">
              <Stethoscope className="w-3.5 h-3.5" /> MBBS in Georgia · SEU
            </div>
            <h1 data-testid="hero-headline" className="font-serif mt-4 sm:mt-6 text-3xl sm:text-5xl lg:text-6xl tracking-tight text-stone-900 leading-[1.05]">
              Guiding students toward their dream of{" "}
              <span className="italic brand-gradient">becoming doctors.</span>
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg text-stone-600 max-w-xl leading-relaxed">
              Your experience matters. Help us improve every step — counselling, admission, visa, hostel,
              food at <span className="italic">Atmia Saffron Kingdom</span> and life on campus at SEU – Georgian National University.
            </p>
            <div className="mt-6 sm:mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
              <Link
                to="/feedback" data-testid="hero-cta-feedback"
                className="inline-flex items-center gap-2 bg-saffron-500 hover:bg-saffron-600 text-white rounded-full px-5 sm:px-7 py-3 sm:py-3.5 text-sm sm:text-base font-medium shadow-md hover:shadow-lg transition-all btn-shine"
              >
                <MessageSquareHeart className="w-4 h-4" /> Share Your Feedback
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#about" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 text-stone-700 hover:text-saffron-600 font-medium text-sm sm:text-base">
                Why Atmia? <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="mt-8 sm:mt-12 grid grid-cols-2 sm:grid-cols-4 gap-5 sm:gap-6 max-w-2xl">
              {stats.map((s) => (
                <div key={s.label} data-testid={`stat-${s.label}`}>
                  <div className="font-serif text-2xl sm:text-3xl text-stone-900">{s.value}</div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-stone-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 mt-8 lg:mt-0"
          >
            <div className="relative max-w-md mx-auto lg:max-w-none">
              <div className="absolute -inset-4 sm:-inset-6 bg-saffron-100/50 rounded-[2rem] -rotate-2" />
              <div className="absolute -inset-3 sm:-inset-4 bg-lime-100/40 rounded-[1.8rem] rotate-1" />
              <img
                src={HERO_IMG} alt="Smiling future doctors"
                className="relative rounded-[1.5rem] sm:rounded-[1.75rem] shadow-xl object-cover aspect-[4/5] w-full"
              />
              <div className="absolute -bottom-5 -left-3 sm:-bottom-6 sm:-left-6 bg-white border border-stone-200 rounded-2xl shadow-lg px-4 sm:px-5 py-3 sm:py-4 flex items-center gap-2.5 sm:gap-3 max-w-[220px] sm:max-w-[240px]">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-saffron-100 text-saffron-600 flex items-center justify-center shrink-0">
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs sm:text-sm font-medium text-stone-900 truncate">Family-first care</div>
                  <div className="text-[10px] sm:text-xs text-stone-500">From counselling to campus.</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" data-testid="about-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-saffron-600 font-medium">About Atmia</div>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl mt-2 sm:mt-3 leading-tight text-stone-900">
              A trusted hand from the first call to the first stethoscope.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 sm:space-y-5 text-stone-700 text-base sm:text-lg leading-relaxed">
            <p>
              Atmia Education partners with families across India to make MBBS abroad simple, safe and rewarding.
              From the first counselling conversation to graduation, we are present at every step — with honesty,
              care and a network that students and parents can trust.
            </p>
            <p>
              At SEU – Georgian National University in Tbilisi, our local team supports students with accommodation,
              wholesome meals at <span className="italic">Atmia Saffron Kingdom</span>, academic coordination and
              everyday wellbeing.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" data-testid="services-section" className="bg-white border-y border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
          <div className="max-w-2xl">
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-saffron-600 font-medium">What We Do</div>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl mt-2 sm:mt-3 text-stone-900">Care across every step of the journey.</h2>
          </div>
          <div className="mt-8 sm:mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`service-card-${s.title.replace(/\s+/g, "-").toLowerCase()}`}
                className="group bg-sand-50 hover:bg-white border border-stone-200 rounded-2xl p-7 transition-all hover:shadow-md hover:-translate-y-0.5"
              >
                <div className="w-11 h-11 rounded-xl bg-saffron-100 text-saffron-600 flex items-center justify-center mb-5 group-hover:bg-saffron-500 group-hover:text-white transition-colors">
                  <s.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-serif text-xl text-stone-900">{s.title}</h3>
                <p className="text-stone-600 mt-2 leading-relaxed text-[15px]">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* UNIVERSITY */}
      <section id="university" data-testid="university-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="text-xs uppercase tracking-[0.2em] text-saffron-600 font-medium">Our University Partner</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-3 leading-tight text-stone-900">
              SEU – Georgian National University, Tbilisi.
            </h2>
            <p className="mt-5 text-stone-600 leading-relaxed text-lg">
              A respected medical destination for Indian students — recognised, English-medium MBBS,
              modern infrastructure and a multicultural campus that feels like home.
            </p>
            <ul className="mt-6 space-y-3 text-stone-700">
              {[
                "Recognised MBBS programme, English medium",
                "Modern campus, labs & academic environment",
                "Indian-friendly community & meals",
                "Atmia local team available 24/7 in Tbilisi",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <GraduationCap className="w-5 h-5 mt-0.5 text-saffron-600" strokeWidth={1.75} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <img src={CAMPUS_IMG} alt="SEU campus" className="rounded-2xl shadow-lg object-cover aspect-[4/3] w-full" />
            <img src={SAFFRON_IMG} alt="Atmia Saffron Kingdom" className="hidden md:block absolute -bottom-10 -left-10 w-44 h-44 object-cover rounded-2xl border-4 border-white shadow-xl" />
          </div>
        </div>
      </section>

      {/* TESTIMONIAL PLACEHOLDER */}
      <section data-testid="testimonial-section" className="bg-stone-900 text-sand-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20 lg:py-24 text-center">
          <Quote className="w-10 h-10 mx-auto text-saffron-400" strokeWidth={1.5} />
          <p className="mt-6 font-serif text-2xl lg:text-3xl leading-snug italic">
            “Atmia walked with our family through every step — the counsellor, the visa, the airport,
            the hostel, even the meals. Our daughter feels at home in Tbilisi.”
          </p>
          <div className="mt-6 text-sm tracking-[0.18em] uppercase text-saffron-300">— A Parent · Ahmedabad</div>
        </div>
      </section>

      {/* Dynamic testimonials from consented responses */}
      <TestimonialWall />

      {/* CTA */}
      <section id="feedback" data-testid="feedback-cta-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
        <div className="relative overflow-hidden rounded-3xl text-white px-6 sm:px-8 lg:px-16 py-12 sm:py-16 lg:py-20 grain-bg"
             style={{ background: "linear-gradient(135deg, #1E90D7 0%, #1576B5 55%, #5F7E18 100%)" }}>
          <div className="relative max-w-2xl">
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-white/80 font-medium">5 minutes · Your voice matters</div>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl mt-3 sm:mt-4 leading-tight">
              Share your feedback. <br className="hidden sm:block" />
              Help us serve the next family better.
            </h2>
            <p className="mt-4 sm:mt-5 text-white/90 text-base sm:text-lg leading-relaxed">
              Tell us about counselling, admission, visa, the SEU campus, hostel, food at Atmia Saffron Kingdom
              and your overall experience. Your honest words shape what we do next.
            </p>
            <Link
              to="/feedback" data-testid="cta-bottom-feedback"
              className="mt-6 sm:mt-8 inline-flex items-center gap-2 bg-ink-900 hover:bg-ink-800 text-white rounded-full px-6 sm:px-8 py-3 sm:py-3.5 text-sm sm:text-base font-medium shadow-md hover:shadow-lg transition-all btn-shine"
            >
              Start Feedback Form <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
